import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import useRouteIdentity from '../../hooks/useRouteIdentity';
import BBALoader from '../BBALoader';
import { REPORTS_BY_ID, canOpenReport, isReportAdminRole } from '../../utils/reportCatalog';
import { ReportIdContext } from './reportCards';

/**
 * Lets a staff member into a CRM report when they are an Owner/Executive or the
 * report's reportAccess doc (or its default) includes them. Sits inside a
 * RoleBasedRoute for internal users, so identity is already loaded.
 */
const ReportAccessGate = ({ reportId, children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { identity } = useRouteIdentity(currentUser, authLoading);
  const isAdmin = isReportAdminRole(identity?.role);
  const [state, setState] = useState({ loaded: false, accessDoc: null });

  useEffect(() => {
    if (isAdmin || !reportId) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'reportAccess', reportId))
      .then((snap) => {
        if (!cancelled) setState({ loaded: true, accessDoc: snap.exists() ? snap.data() : null });
      })
      .catch((error) => {
        // Fall back to the report's default access rather than locking the page
        console.error('Error loading report access:', error);
        if (!cancelled) setState({ loaded: true, accessDoc: null });
      });
    return () => { cancelled = true; };
  }, [isAdmin, reportId]);

  // Cards on the page pin under this report's id (see reportCards.js)
  const page = <ReportIdContext.Provider value={reportId}>{children}</ReportIdContext.Provider>;

  if (isAdmin) return page;

  if (!state.loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BBALoader size={128} />
      </div>
    );
  }

  return canOpenReport(REPORTS_BY_ID[reportId], state.accessDoc, identity?.role, currentUser?.uid)
    ? page
    : <Navigate to="/crm" replace />;
};

export default ReportAccessGate;
